/* ITER239 · Phase 2 — Petty Cash Wallets & Transfers
 *
 * Asymmetric refill model surfaced to the user:
 *   • Sales-cash transfer → PENDING, admin must approve before wallet credit.
 *   • Accounts replenishment → posted directly, wallet credited immediately.
 *
 * Store→Admin fallback: when the outlet has store_petty_cash_enabled=false,
 * the Store routing resolves to the tenant Admin central pool. The dialog
 * surfaces this resolution before the user confirms.
 */
import React, { useEffect, useMemo, useState, useCallback } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';

import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  ArrowLeft, ArrowDownToLine, ArrowRightFromLine, Banknote, Check, Layers, Loader2, MoreVertical, Pencil, Plus, Power, PowerOff, RefreshCw, Settings, Trash2, Wallet, X,
} from 'lucide-react';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ADMIN_ROLES = new Set(['admin', 'area_manager', 'super_admin']);
const fmt = (n) => `₹${Number(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const SOURCE_META = {
  sales_cash: {
    label: 'Transfer from sales cash',
    badge: 'Awaits admin approval',
    color: 'border-amber-300 bg-amber-50 text-amber-900',
    icon: ArrowRightFromLine,
    description: 'Manager initiates · admin approves · wallet credits on approval.',
  },
  accounts: {
    label: 'Replenishment from accounts',
    badge: 'Posted directly',
    color: 'border-emerald-300 bg-emerald-50 text-emerald-900',
    icon: ArrowDownToLine,
    description: 'Manager records · wallet credits immediately · no admin step.',
  },
};

// ─── Transfer / Replenishment create dialog ─────────────────────────
function TransferDialog({ open, onClose, outlets, defaultOutletId, source, onSaved, walletsByOutlet }) {
  const { getHeaders } = useAuth();
  const [outletId, setOutletId] = useState(defaultOutletId || '');
  const [walletType, setWalletType] = useState('ops');
  const [amount, setAmount] = useState('');
  const [reference, setReference] = useState('');
  const [notes, setNotes] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open) return;
    setOutletId(defaultOutletId || outlets[0]?.id || '');
    setWalletType('ops');
    setAmount('');
    setReference('');
    setNotes('');
    setError('');
  }, [open, defaultOutletId, outlets]);

  const selectedOutlet = outlets.find(o => o.id === outletId);
  // ITER314 — the destination wallet list is now dynamic. Filter to active
  // wallets for the selected outlet (excluding the tenant admin pool).
  const outletWallets = (walletsByOutlet?.[outletId] || []).filter(
    (w) => w.wallet_type !== 'admin' && w.active !== false,
  );
  // Ensure the selected wallet remains valid when the outlet changes.
  useEffect(() => {
    if (!open) return;
    if (outletWallets.length && !outletWallets.some(w => w.wallet_type === walletType)) {
      setWalletType(outletWallets[0].wallet_type);
    }
  }, [outletId, outletWallets, open, walletType]);

  const storeFallback = walletType === 'store' && !selectedOutlet?.store_petty_cash_enabled;
  const selectedWallet = outletWallets.find(w => w.wallet_type === walletType);
  const effectiveLabel = walletType === 'ops'
    ? 'Operations wallet (outlet)'
    : walletType === 'store'
      ? (selectedOutlet?.store_petty_cash_enabled
          ? 'Store wallet (outlet)'
          : 'Admin central pool (Store→Admin fallback)')
      : `${selectedWallet?.name || walletType} wallet (outlet)`;

  const meta = SOURCE_META[source];
  const Icon = meta?.icon || Banknote;

  const submit = async () => {
    setError('');
    if (!outletId) return toast.error('Select an outlet');
    if (!Number(amount) || Number(amount) <= 0) return toast.error('Enter a valid amount');
    setBusy(true);
    try {
      await axios.post(`${API}/petty-cash/transfers`, {
        outlet_id: outletId,
        wallet_type: walletType,
        amount: Number(amount),
        source,
        reference: reference.trim(),
        notes: notes.trim(),
      }, { headers: getHeaders() });
      toast.success(
        source === 'sales_cash'
          ? 'Transfer submitted — awaiting admin approval'
          : 'Replenishment recorded — wallet credited'
      );
      onSaved?.();
      onClose();
    } catch (err) {
      const detail = err.response?.data?.detail || 'Could not create transfer';
      setError(detail);
      toast.error(detail);
    } finally {
      setBusy(false);
    }
  };

  if (!meta) return null;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-md max-h-[90vh] overflow-y-auto" data-testid={`transfer-dialog-${source}`}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-[#1A2421]">
            <Icon className="w-4 h-4" /> {meta.label}
          </DialogTitle>
          <DialogDescription className="text-[#5C6B64]">{meta.description}</DialogDescription>
        </DialogHeader>

        <div className={`rounded border px-3 py-2 text-[11px] leading-snug ${meta.color}`}>
          <strong>{meta.badge}.</strong>{' '}
          {source === 'sales_cash'
            ? 'No cash hits the wallet until an admin approves this entry.'
            : 'This funds the wallet now. An immutable ledger row records the deposit.'}
        </div>

        <div className="space-y-3 mt-2">
          <div className="space-y-1.5">
            <Label className="text-sm text-[#5C6B64]">Outlet <span className="text-[#C45A41]">*</span></Label>
            <Select value={outletId} onValueChange={setOutletId}>
              <SelectTrigger data-testid="transfer-outlet-select" className="bg-[#F9F9F8] border-[#E2E8DF] text-[12px]">
                <SelectValue placeholder="Select outlet" />
              </SelectTrigger>
              <SelectContent>
                {outlets.map(o => <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1.5">
            <Label className="text-sm text-[#5C6B64] flex items-center gap-1.5">
              <Wallet className="w-3.5 h-3.5" /> Destination wallet <span className="text-[#C45A41]">*</span>
            </Label>
            <div className="grid grid-cols-2 gap-2">
              {outletWallets.length === 0 && (
                <p className="col-span-2 text-[11px] text-[#5C6B64] italic">
                  No wallets configured for this outlet yet.
                </p>
              )}
              {outletWallets.map(w => {
                const wt = w.wallet_type;
                const isReserved = wt === 'ops' || wt === 'store';
                const label = w.name || (wt === 'ops' ? 'Operations' : wt === 'store' ? 'Store / Provisions' : wt);
                const sub = isReserved
                  ? (wt === 'store' && !selectedOutlet?.store_petty_cash_enabled
                      ? 'Store wallet not enabled — credits Admin central pool.'
                      : wt === 'ops' ? 'Outlet operational petty cash.' : 'Outlet store petty cash.')
                  : (w.description || `Departmental petty cash — ${label}`);
                return (
                  <button
                    key={wt} type="button"
                    data-testid={`transfer-wallet-${wt}`}
                    onClick={() => { setWalletType(wt); setError(''); }}
                    className={`text-left rounded border px-3 py-2 transition ${walletType === wt
                      ? 'border-[color:var(--color-primary)] bg-[color:var(--color-primary)]/[0.06]'
                      : 'border-[#E2E8DF] bg-white hover:bg-[#F9F9F8]'}`}
                  >
                    <span className="text-[12px] font-semibold text-[#1A2421]">{label}</span>
                    <p className="text-[10px] text-[#5C6B64] mt-0.5 line-clamp-2">{sub}</p>
                  </button>
                );
              })}
            </div>
            <p className="text-[10px] text-[#5C6B64]">
              Resolves to: <strong className="text-[#1A2421]">{effectiveLabel}</strong>
              {storeFallback && (
                <span className="text-[#7A2E1F]"> — fallback active</span>
              )}
            </p>
          </div>

          <div className="space-y-1.5">
            <Label className="text-sm text-[#5C6B64]">Amount (₹) <span className="text-[#C45A41]">*</span></Label>
            <Input
              type="number" min="1" step="1" value={amount}
              onChange={(e) => setAmount(e.target.value)}
              className="bg-[#F9F9F8] border-[#E2E8DF] text-[12px]"
              data-testid="transfer-amount" placeholder="0" />
          </div>

          <div className="space-y-1.5">
            <Label className="text-sm text-[#5C6B64]">Reference</Label>
            <Input
              value={reference}
              onChange={(e) => setReference(e.target.value)}
              placeholder={source === 'accounts' ? 'UTR / cheque #' : 'Cash deposit slip #'}
              className="bg-[#F9F9F8] border-[#E2E8DF] text-[12px]"
              data-testid="transfer-reference" />
          </div>

          <div className="space-y-1.5">
            <Label className="text-sm text-[#5C6B64]">Notes</Label>
            <Textarea rows={2} value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Optional — short context for the audit trail"
              className="bg-[#F9F9F8] border-[#E2E8DF] text-[12px]"
              data-testid="transfer-notes" />
          </div>

          {error && (
            <div className="rounded border border-[#C45A41]/40 bg-[#C45A41]/[0.06] px-3 py-2 text-[12px] text-[#7A2E1F]"
              data-testid="transfer-submit-error">
              {error}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}
            className="border-[#E2E8DF] text-[#5C6B64]" data-testid="transfer-cancel">Cancel</Button>
          <Button disabled={busy} onClick={submit}
            className="bg-[color:var(--color-primary)] hover:bg-[color:var(--color-primary-hover)] text-white"
            data-testid="transfer-submit">
            {busy ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Check className="w-3 h-3 mr-1" />}
            {source === 'sales_cash' ? 'Submit for Approval' : 'Record Replenishment'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


// ─── Admin reject dialog ─────────────────────────────────────────────
function RejectTransferDialog({ open, onClose, transfer, onDone }) {
  const { getHeaders } = useAuth();
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) { setReason(''); setBusy(false); } }, [open]);

  if (!transfer) return null;
  const submit = async () => {
    if (!reason.trim()) return toast.error('Reason is required to reject');
    setBusy(true);
    try {
      await axios.put(`${API}/petty-cash/transfers/${transfer.id}/reject`,
        { reason: reason.trim() }, { headers: getHeaders() });
      toast.success('Transfer rejected — no wallet movement');
      onDone?.();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not reject transfer');
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-sm" data-testid="reject-transfer-dialog">
        <DialogHeader>
          <DialogTitle className="text-[#1A2421] flex items-center gap-2">
            <X className="w-4 h-4 text-rose-600" /> Reject Transfer
          </DialogTitle>
          <DialogDescription className="text-[#5C6B64]">
            {fmt(transfer.amount)} · {transfer.wallet_type.toUpperCase()} ·
            {' '}{transfer.created_by?.name || '—'}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-1.5">
          <Label className="text-sm text-[#5C6B64]">Reason for rejection <span className="text-[#C45A41]">*</span></Label>
          <Textarea rows={3} value={reason} onChange={(e) => setReason(e.target.value)}
            placeholder="e.g. denomination mismatch, missing slip…"
            className="bg-[#F9F9F8] border-[#E2E8DF] text-[12px]"
            data-testid="reject-transfer-reason" />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}
            className="border-[#E2E8DF] text-[#5C6B64]" data-testid="reject-transfer-cancel">Cancel</Button>
          <Button disabled={busy || !reason.trim()} onClick={submit}
            className="bg-rose-600 hover:bg-rose-700 text-white"
            data-testid="reject-transfer-submit">
            {busy ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <X className="w-3 h-3 mr-1" />}
            Reject
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── ITER311.6 · Admin-only amendment dialog ─────────────────────────
function AmendTransferDialog({ open, onClose, transfer, onDone }) {
  const { getHeaders } = useAuth();
  const [amount, setAmount] = useState('');
  const [reference, setReference] = useState('');
  const [notes, setNotes] = useState('');
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [auditTrail, setAuditTrail] = useState([]);

  useEffect(() => {
    if (open && transfer) {
      setAmount(String(transfer.amount ?? ''));
      setReference(transfer.reference ?? '');
      setNotes(transfer.notes ?? '');
      setReason('');
      setBusy(false);
      // Load prior amendments for context.
      axios.get(`${API}/petty-cash/transfers/${transfer.id}/audit`,
                 { headers: getHeaders() })
        .then(r => setAuditTrail(r.data?.amendments || []))
        .catch(() => setAuditTrail([]));
    }
  }, [open, transfer, getHeaders]);

  if (!transfer) return null;
  const prevAmount = Number(transfer.amount || 0);
  const nextAmount = Number(amount || 0);
  const delta = nextAmount - prevAmount;

  const submit = async () => {
    if (!reason.trim()) return toast.error('Amendment reason is required');
    if (!(nextAmount > 0)) return toast.error('Amount must be > 0');
    setBusy(true);
    try {
      await axios.put(`${API}/petty-cash/transfers/${transfer.id}/amend`,
        {
          amount: nextAmount,
          reference: reference,
          notes: notes,
          reason: reason.trim(),
        },
        { headers: getHeaders() });
      toast.success(`Transfer amended · wallet Δ ₹${delta.toLocaleString('en-IN')}`);
      onDone?.();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not amend transfer');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-md max-h-[90vh] overflow-y-auto" data-testid="amend-transfer-dialog">
        <DialogHeader>
          <DialogTitle className="text-[#1A2421] flex items-center gap-2">
            <Pencil className="w-4 h-4 text-[color:var(--color-primary)]" /> Amend Transfer
          </DialogTitle>
          <DialogDescription className="text-[#5C6B64] text-[11px] leading-relaxed">
            Source & destination wallets and the original date cannot be changed —
            open a new transfer if those need correcting. Every field change is
            logged with your identity and appears on the audit trail.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div className="rounded border border-[#E2E8DF] bg-[#F9F9F8] px-3 py-2 text-[11px] text-[#5C6B64] leading-snug"
                data-testid="amend-locked-fields">
            <div><b className="text-[#1A2421]">Wallet:</b> {transfer.wallet_type.toUpperCase()}</div>
            <div><b className="text-[#1A2421]">Original date:</b> {(transfer.created_at || '').slice(0, 16).replace('T', ' ')}</div>
            <div><b className="text-[#1A2421]">Source:</b> {transfer.source === 'accounts' ? 'Replenishment' : 'Sales cash'}</div>
          </div>

          <div>
            <Label className="text-[11px] uppercase tracking-wider text-[#5C6B64]">Amount (₹)</Label>
            <Input type="number" step="0.01" value={amount}
                    onChange={(e) => setAmount(e.target.value)}
                    className="h-10 border-[#E2E8DF]"
                    data-testid="amend-amount-input" />
            {delta !== 0 && Number.isFinite(delta) && (
              <div className={`text-[11px] mt-1 font-semibold ${delta > 0 ? 'text-emerald-700' : 'text-rose-700'}`}
                    data-testid="amend-delta-preview">
                Δ {delta > 0 ? '+' : ''}₹{Math.abs(delta).toLocaleString('en-IN')}
                {' '}will {delta > 0 ? 'credit' : 'debit'} the {transfer.wallet_type.toUpperCase()} wallet.
              </div>
            )}
          </div>

          <div>
            <Label className="text-[11px] uppercase tracking-wider text-[#5C6B64]">Reference</Label>
            <Input value={reference}
                    onChange={(e) => setReference(e.target.value)}
                    className="h-10 border-[#E2E8DF]"
                    data-testid="amend-reference-input"
                    placeholder="UTR / cheque no / receipt" />
          </div>

          <div>
            <Label className="text-[11px] uppercase tracking-wider text-[#5C6B64]">Notes</Label>
            <Textarea rows={2} value={notes}
                       onChange={(e) => setNotes(e.target.value)}
                       className="border-[#E2E8DF] text-[12px]"
                       data-testid="amend-notes-input" />
          </div>

          <div>
            <Label className="text-[11px] uppercase tracking-wider text-[#5C6B64]">
              Reason for amendment <span className="text-[#C45A41]">*</span>
              <span className="ml-2 text-[10px] normal-case font-normal text-[#5C6B64]">
                ({reason.length}/1000)
              </span>
            </Label>
            <Textarea rows={3} value={reason}
                       onChange={(e) => setReason(e.target.value.slice(0, 1000))}
                       placeholder="Finance auditors need context — why is this correction being made?"
                       className="bg-[#F9F9F8] border-[#E2E8DF] text-[12px]"
                       data-testid="amend-reason-input" />
          </div>

          {auditTrail.length > 0 && (
            <div className="rounded border border-amber-200 bg-amber-50 px-3 py-2 text-[11px] text-amber-900"
                  data-testid="amend-prior-trail">
              <div className="font-semibold mb-1">Prior amendments ({auditTrail.length})</div>
              <ul className="space-y-1 max-h-32 overflow-y-auto">
                {auditTrail.map((a) => (
                  <li key={a.amendment_id} className="leading-snug">
                    <span className="tabular-nums">{(a.at || '').slice(0, 16).replace('T', ' ')}</span>
                    {' · '}{a.by?.name || a.by?.email || '—'}
                    {a.changes?.amount && (
                      <> · ₹{a.changes.amount.prev.toLocaleString('en-IN')} → ₹{a.changes.amount.new.toLocaleString('en-IN')}</>
                    )}
                    {' · '}<i>{a.reason}</i>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}
                  className="border-[#E2E8DF] text-[#5C6B64]"
                  data-testid="amend-transfer-cancel">Cancel</Button>
          <Button disabled={busy || !reason.trim() || !(nextAmount > 0)} onClick={submit}
                  className="bg-[color:var(--color-primary)] hover:bg-[color:var(--color-primary-hover)] text-white"
                  data-testid="amend-transfer-submit">
            {busy ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Pencil className="w-3 h-3 mr-1" />}
            Save amendment
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}



// ─── Page ────────────────────────────────────────────────────────────
export default function PettyCashWallets() {
  const { user, getHeaders } = useAuth();
  const isAdmin = ADMIN_ROLES.has(user?.role || '');
  const [outlets, setOutlets] = useState([]);
  const [wallets, setWallets] = useState([]);
  const [transfers, setTransfers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedOutletId, setSelectedOutletId] = useState('');
  const [openSource, setOpenSource] = useState(null);   // 'sales_cash' | 'accounts'
  const [rejectTarget, setRejectTarget] = useState(null);
  const [actionId, setActionId] = useState(null);
  const [togglingWalletId, setTogglingWalletId] = useState(null);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const outletsPath = isAdmin ? `${API}/admin/outlets` : `${API}/outlets`;
      const [or, wr, tr] = await Promise.all([
        axios.get(outletsPath, { headers: getHeaders() }).catch(() => ({ data: [] })),
        axios.get(`${API}/petty-cash/wallets`, { headers: getHeaders() }),
        axios.get(`${API}/petty-cash/transfers`, { headers: getHeaders() }),
      ]);
      setOutlets(or.data || []);
      setWallets(wr.data || []);
      setTransfers(tr.data || []);
      if (!selectedOutletId) {
        const first = (or.data || [])[0]?.id || '';
        setSelectedOutletId(first);
      }
    } catch (e) {
      toast.error('Could not load wallets');
    } finally {
      setLoading(false);
    }
  }, [getHeaders, isAdmin, selectedOutletId]);

  useEffect(() => { reload(); }, [reload]);

  const outletWallets = useMemo(() => {
    const out = { ops: null, store: null };
    for (const w of wallets) {
      if (w.outlet_id === selectedOutletId) {
        if (w.wallet_type === 'ops') out.ops = w;
        if (w.wallet_type === 'store') out.store = w;
      }
    }
    return out;
  }, [wallets, selectedOutletId]);

  // ITER314 — All wallets for the currently-selected outlet, plus every
  // outlet's wallets keyed by outlet_id (used by the transfer dialog).
  const outletWalletList = useMemo(
    () => wallets.filter(w => w.outlet_id === selectedOutletId),
    [wallets, selectedOutletId],
  );
  const walletsByOutlet = useMemo(() => {
    const map = {};
    for (const w of wallets) {
      if (!w.outlet_id) continue;
      (map[w.outlet_id] = map[w.outlet_id] || []).push(w);
    }
    return map;
  }, [wallets]);
  const customDeptWallets = useMemo(
    () => outletWalletList.filter(w => !['ops', 'store', 'admin'].includes(w.wallet_type)),
    [outletWalletList],
  );

  // Modal state for Create / Edit / Delete wallet.
  const [createOpen, setCreateOpen] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const adminWallet = useMemo(
    () => wallets.find(w => w.outlet_id == null && w.wallet_type === 'admin'),
    [wallets]
  );

  const pendingTransfers = useMemo(
    () => transfers.filter(t => t.status === 'pending'),
    [transfers]
  );
  const recentTransfers = useMemo(
    () => transfers.filter(t => t.status !== 'pending').slice(0, 10),
    [transfers]
  );
  // ITER311.6 · History = every non-pending transfer beyond the recent 10.
  const historyTransfers = useMemo(
    () => transfers.filter(t => t.status !== 'pending').slice(10),
    [transfers]
  );
  const [showHistory, setShowHistory] = useState(false);
  const [amendTarget, setAmendTarget] = useState(null);

  const selectedOutlet = outlets.find(o => o.id === selectedOutletId);

  const approveTransfer = async (t) => {
    setActionId(t.id);
    try {
      await axios.put(`${API}/petty-cash/transfers/${t.id}/approve`,
        { reason: '' }, { headers: getHeaders() });
      toast.success('Approved — wallet credited');
      await reload();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not approve');
    } finally {
      setActionId(null);
    }
  };

  // ITER314.1 — unified enable/disable for every wallet. System wallets
  // (ops / store / admin) go through the same PATCH — the backend also
  // syncs outlets.store_petty_cash_enabled when the Store wallet toggles.
  const toggleWalletActive = async (w) => {
    if (!w?.id) return;
    const willDisable = w.active !== false;
    if (willDisable) {
      const ok = window.confirm(
        `Disable "${w.name || w.wallet_type}"?\n\n`
        + 'Users will not be able to post new vouchers or transfers to '
        + 'this wallet. History remains fully accessible in reports.'
      );
      if (!ok) return;
    }
    setTogglingWalletId(w.id);
    try {
      await axios.patch(`${API}/petty-cash/wallets/${w.id}`,
        { active: !willDisable }, { headers: getHeaders() });
      toast.success(willDisable ? 'Wallet disabled' : 'Wallet enabled');
      await reload();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not update wallet');
    } finally {
      setTogglingWalletId(null);
    }
  };

  const deleteWallet = async (w) => {
    if (!w?.id) return;
    setTogglingWalletId(w.id);
    try {
      await axios.delete(`${API}/petty-cash/wallets/${w.id}`, { headers: getHeaders() });
      toast.success('Wallet deleted');
      setDeleteTarget(null);
      await reload();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not delete wallet');
    } finally {
      setTogglingWalletId(null);
    }
  };

  const backHref = isAdmin ? '/admin/petty-cash' : '/manager/petty-cash';

  return (
    <div className="space-y-4" data-testid="petty-cash-wallets-page">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-3">
          <Link to={backHref}
            className="inline-flex items-center gap-1 text-[12px] text-[#5C6B64] hover:text-[#1A2421]"
            data-testid="back-to-vouchers">
            <ArrowLeft className="w-3.5 h-3.5" /> Back to vouchers
          </Link>
          <div>
            <h1 className="text-lg sm:text-xl font-semibold tracking-tight text-[#1A2421]">Wallets & Transfers</h1>
            <p className="text-[12px] text-[#5C6B64]">Wallet balances · sales-cash transfers · accounts replenishments</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {outlets.length > 1 && (
            <Select value={selectedOutletId} onValueChange={setSelectedOutletId}>
              <SelectTrigger className="h-8 text-[12px] w-[200px] bg-white border-[#E2E8DF]" data-testid="outlet-switcher">
                <SelectValue placeholder="Outlet" />
              </SelectTrigger>
              <SelectContent>
                {outlets.map(o => <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>)}
              </SelectContent>
            </Select>
          )}
          <Button variant="outline" onClick={reload}
            className="h-8 text-[12px] gap-1 border-[#E2E8DF] text-[#1A2421]" data-testid="reload-wallets">
            <RefreshCw className="w-3.5 h-3.5" /> Refresh
          </Button>
          {isAdmin && (
            <>
              <Link to="/admin/petty-cash/categories"
                className="h-8 text-[12px] gap-1 inline-flex items-center px-3 rounded border border-[#E2E8DF] bg-white text-[#1A2421] hover:bg-[#F4F6F2]"
                data-testid="open-categories-link">
                <Layers className="w-3.5 h-3.5" /> Categories
              </Link>
              <Button
                onClick={() => setCreateOpen(true)}
                className="h-8 text-[12px] gap-1 bg-[color:var(--color-primary)] hover:bg-[color:var(--color-primary-hover)] text-white"
                data-testid="create-wallet-btn">
                <Plus className="w-3.5 h-3.5" /> Create Wallet
              </Button>
            </>
          )}
        </div>
      </div>

      {loading ? (
        <div className="text-[12px] text-[#5C6B64]">Loading…</div>
      ) : (
        <>
          {/* Wallet cards — ITER314.1 unified grid.
              Built-in trio (Operations, Store, Admin) always renders first,
              then default-departmental (Maintenance) and custom wallets in
              the order they arrive from the API. EVERY card has the same
              ⋮ Settings / Enable-Disable / Delete menu — no per-type
              controls. Held / Available surfaces are hidden. */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3" data-testid="wallet-cards">
            {(() => {
              const cards = [];
              const opsW = outletWallets.ops;
              cards.push({
                key: 'ops',
                testid: 'wallet-card-ops',
                label: opsW?.name || 'Operations',
                wallet: opsW,
                sub: selectedOutlet ? `${selectedOutlet.name} · operational petty cash` : '',
              });
              const showStore = !!selectedOutlet?.store_petty_cash_enabled;
              cards.push({
                key: 'store',
                testid: 'wallet-card-store',
                label: outletWallets.store?.name || 'Store / Provisions',
                wallet: showStore ? outletWallets.store : null,
                sub: showStore
                  ? `${selectedOutlet?.name} · store petty cash`
                  : 'Store not enabled — Store routing falls back to Admin pool',
                fallback: !showStore,
              });
              cards.push({
                key: 'admin',
                testid: 'wallet-card-admin',
                label: adminWallet?.name || 'Admin central pool',
                wallet: adminWallet,
                sub: 'Tenant-wide · receives Store fallback',
              });
              for (const w of customDeptWallets) {
                cards.push({
                  key: w.wallet_type,
                  testid: `wallet-card-${w.wallet_type}`,
                  label: w.name || w.wallet_type,
                  wallet: w,
                  sub: w.description || `${selectedOutlet?.name || ''} · departmental petty cash`,
                });
              }
              return cards.map(c => (
                <WalletCard
                  key={c.key}
                  label={c.label}
                  wallet={c.wallet}
                  sub={c.sub}
                  fallback={c.fallback}
                  testid={c.testid}
                  isAdmin={isAdmin}
                  onEdit={() => setEditTarget(c.wallet)}
                  onToggleActive={() => toggleWalletActive(c.wallet)}
                  onDelete={() => setDeleteTarget(c.wallet)}
                  toggling={togglingWalletId === c.wallet?.id}
                />
              ));
            })()}
          </div>

          {/* Action buttons */}
          <div className="flex items-center gap-2 flex-wrap" data-testid="transfer-actions">
            <Button onClick={() => setOpenSource('sales_cash')}
              className="bg-[color:var(--color-primary)] hover:bg-[color:var(--color-primary-hover)] text-white h-8 text-xs gap-1"
              data-testid="open-sales-cash-transfer">
              <ArrowRightFromLine className="w-3.5 h-3.5" /> Transfer from Sales Cash
            </Button>
            <Button onClick={() => setOpenSource('accounts')}
              className="bg-emerald-700 hover:bg-emerald-800 text-white h-8 text-xs gap-1"
              data-testid="open-accounts-replenishment">
              <ArrowDownToLine className="w-3.5 h-3.5" /> Record Replenishment
            </Button>
            <span className="text-[10px] text-[#5C6B64] ml-2">
              Sales cash needs admin approval · accounts replenishment posts directly
            </span>
          </div>

          {/* Pending queue (admin actionable; manager-readable) */}
          <Card className="border-[#E2E8DF]" data-testid="pending-transfers-card">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm text-[#1A2421] flex items-center gap-2">
                <Banknote className="w-4 h-4 text-amber-700" /> Pending sales-cash transfers
                <span className="text-[11px] text-[#5C6B64] font-normal ml-1">({pendingTransfers.length})</span>
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-0">
              {pendingTransfers.length === 0 ? (
                <p className="text-[12px] text-[#5C6B64]" data-testid="pending-empty">
                  No pending transfers. Cash from sales waits here until an admin approves.
                </p>
              ) : (
                <div className="space-y-2">
                  {pendingTransfers.map(t => {
                    const outletName = outlets.find(o => o.id === t.outlet_id)?.name || t.outlet_id;
                    const fellBack = t.wallet_type_requested === 'store' && t.wallet_type === 'admin';
                    return (
                      <div key={t.id}
                        className="rounded border border-amber-200 bg-amber-50/40 px-3 py-2 flex items-start justify-between gap-3"
                        data-testid={`pending-transfer-${t.id}`}>
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="text-[13px] font-semibold text-[#1A2421] tabular-nums">{fmt(t.amount)}</span>
                            <span className="text-[10px] uppercase tracking-wider text-[#5C6B64]">
                              → {t.wallet_type.toUpperCase()}
                              {fellBack && <span className="text-[#7A2E1F]"> (fallback)</span>}
                            </span>
                            <span className="text-[10px] text-[#5C6B64]">· {outletName}</span>
                          </div>
                          <p className="text-[11px] text-[#5C6B64] mt-0.5">
                            {t.created_by?.name || '—'} · {(t.created_at || '').slice(0, 16).replace('T', ' ')}
                            {t.reference && <span className="ml-2">· ref: {t.reference}</span>}
                          </p>
                          {t.notes && <p className="text-[11px] text-[#1A2421] mt-0.5">{t.notes}</p>}
                        </div>
                        {isAdmin && (
                          <div className="flex gap-1 shrink-0">
                            <Button
                              size="sm" disabled={actionId === t.id}
                              onClick={() => approveTransfer(t)}
                              className="h-7 text-[11px] px-2 gap-1 bg-emerald-700 hover:bg-emerald-800 text-white"
                              data-testid={`approve-transfer-${t.id}`}>
                              {actionId === t.id
                                ? <Loader2 className="w-3 h-3 animate-spin" />
                                : <Check className="w-3 h-3" />} Approve
                            </Button>
                            <Button
                              size="sm" variant="outline"
                              onClick={() => setRejectTarget(t)}
                              className="h-7 text-[11px] px-2 gap-1 border-rose-200 text-rose-700 hover:bg-rose-50"
                              data-testid={`reject-transfer-${t.id}`}>
                              <X className="w-3 h-3" /> Reject
                            </Button>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Recent decided transfers — quick history */}
          {recentTransfers.length > 0 && (
            <Card className="border-[#E2E8DF]" data-testid="recent-transfers-card">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm text-[#1A2421]">Recent transfers · last 10</CardTitle>
              </CardHeader>
              <CardContent className="pt-0">
                <div className="space-y-1">
                  {recentTransfers.map(t => (
                    <div key={t.id}
                      className="flex items-center justify-between border-b border-[#F0F2EE] last:border-0 py-1.5 text-[11px]"
                      data-testid={`recent-transfer-${t.id}`}>
                      <div className="flex items-center gap-2 min-w-0 flex-wrap">
                        <span className={`px-1.5 py-0.5 rounded text-[9px] font-semibold uppercase tracking-wider ${t.status === 'approved'
                          ? 'bg-emerald-100 text-emerald-800'
                          : 'bg-rose-100 text-rose-800'}`}>{t.status}</span>
                        <span className="text-[#1A2421] font-semibold tabular-nums">{fmt(t.amount)}</span>
                        <span className="text-[#5C6B64]">
                          {t.source === 'accounts' ? 'Replenishment' : 'Sales cash'} · {t.wallet_type.toUpperCase()}
                        </span>
                        {(t.audit_log || []).length > 0 && (
                          <span className="px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 text-[9px] font-semibold uppercase tracking-wider"
                                 data-testid={`amended-badge-${t.id}`}>
                            Amended · {(t.audit_log || []).length}
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        <div className="text-[10px] text-[#5C6B64] tabular-nums">
                          {(t.decided_at || t.created_at || '').slice(0, 16).replace('T', ' ')}
                        </div>
                        {isAdmin && t.status === 'approved' && (
                          <Button size="sm" variant="ghost"
                                  onClick={() => setAmendTarget(t)}
                                  className="h-6 px-2 text-[10px] text-[color:var(--color-primary)] hover:bg-[color:var(--color-primary)]/5"
                                  data-testid={`amend-btn-${t.id}`}>
                            Amend
                          </Button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>

                {/* ITER311.6 · Older transfers collapse into a searchable archive.
                    Historical rows are read-only per spec — no amend action. */}
                {historyTransfers.length > 0 && (
                  <div className="mt-3 pt-3 border-t border-[#F0F2EE]">
                    <button
                      type="button"
                      onClick={() => setShowHistory(s => !s)}
                      className="text-[11px] font-semibold text-[color:var(--color-primary)] hover:underline"
                      data-testid="toggle-history-btn"
                    >
                      {showHistory ? '▾' : '▸'} History · {historyTransfers.length} older transfer{historyTransfers.length === 1 ? '' : 's'}
                    </button>
                    {showHistory && (
                      <div className="mt-2 space-y-1 max-h-64 overflow-y-auto"
                            data-testid="history-transfers-list">
                        {historyTransfers.map(t => (
                          <div key={t.id}
                            className="flex items-center justify-between border-b border-[#F0F2EE] last:border-0 py-1.5 text-[11px]"
                            data-testid={`history-transfer-${t.id}`}>
                            <div className="flex items-center gap-2 min-w-0 flex-wrap">
                              <span className={`px-1.5 py-0.5 rounded text-[9px] font-semibold uppercase tracking-wider ${t.status === 'approved'
                                ? 'bg-slate-100 text-slate-700'
                                : 'bg-rose-100 text-rose-800'}`}>{t.status}</span>
                              <span className="text-[#1A2421] font-semibold tabular-nums">{fmt(t.amount)}</span>
                              <span className="text-[#5C6B64]">
                                {t.source === 'accounts' ? 'Replenishment' : 'Sales cash'} · {t.wallet_type.toUpperCase()}
                              </span>
                              {(t.audit_log || []).length > 0 && (
                                <span className="px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 text-[9px] font-semibold uppercase tracking-wider">
                                  Amended · {(t.audit_log || []).length}
                                </span>
                              )}
                            </div>
                            <div className="text-[10px] text-[#5C6B64] tabular-nums shrink-0">
                              {(t.decided_at || t.created_at || '').slice(0, 16).replace('T', ' ')}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          )}
        </>
      )}

      <TransferDialog
        open={!!openSource}
        source={openSource}
        outlets={outlets}
        defaultOutletId={selectedOutletId}
        walletsByOutlet={walletsByOutlet}
        onClose={() => setOpenSource(null)}
        onSaved={reload} />
      <RejectTransferDialog
        open={!!rejectTarget}
        transfer={rejectTarget}
        onClose={() => setRejectTarget(null)}
        onDone={reload} />
      <AmendTransferDialog
        open={!!amendTarget}
        transfer={amendTarget}
        onClose={() => setAmendTarget(null)}
        onDone={reload} />
      <CustomWalletDialog
        open={createOpen}
        mode="create"
        outlets={outlets}
        defaultOutletId={selectedOutletId}
        onClose={() => setCreateOpen(false)}
        onSaved={reload} />
      <CustomWalletDialog
        open={!!editTarget}
        mode="edit"
        wallet={editTarget}
        outlets={outlets}
        onClose={() => setEditTarget(null)}
        onSaved={reload} />
      <DeleteWalletDialog
        open={!!deleteTarget}
        wallet={deleteTarget}
        busy={togglingWalletId === deleteTarget?.id}
        onClose={() => setDeleteTarget(null)}
        onConfirm={() => deleteWallet(deleteTarget)} />
    </div>
  );
}


function DeleteWalletDialog({ open, wallet, busy, onClose, onConfirm }) {
  if (!open || !wallet) return null;
  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
      onClick={() => !busy && onClose()}
      data-testid="delete-wallet-dialog">
      <div className="bg-white rounded-lg border border-[#E2E8DF] p-5 w-full max-w-md space-y-3"
        onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start gap-3">
          <div className="rounded-full bg-rose-50 border border-rose-200 p-2">
            <Trash2 className="w-4 h-4 text-rose-700" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="text-sm font-semibold text-[#1A2421]">Delete {'"'}{wallet.name || wallet.wallet_type}{'"'}?</h3>
            <p className="text-[11px] text-[#5C6B64] mt-1 leading-snug">
              This wallet has no history and no cash. Deleting it is permanent
              and removes it from every future selector. Historical wallets
              cannot be deleted — disable instead.
            </p>
          </div>
        </div>
        <div className="flex items-center justify-end gap-2 pt-1">
          <Button variant="outline" onClick={onClose} disabled={busy}
            className="h-8 text-[12px] border-[#E2E8DF] text-[#5C6B64]"
            data-testid="delete-wallet-cancel">Cancel</Button>
          <Button onClick={onConfirm} disabled={busy}
            className="h-8 text-[12px] bg-rose-700 hover:bg-rose-800 text-white gap-1"
            data-testid="delete-wallet-confirm">
            {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Trash2 className="w-3 h-3" />}
            Delete Wallet
          </Button>
        </div>
      </div>
    </div>
  );
}


function WalletCard({ label, wallet, sub, fallback, testid, isAdmin, onEdit, onToggleActive, onDelete, toggling }) {
  // ITER314.1 — Every card renders with an identical ⋮ menu (Settings /
  // Enable-Disable / Delete). No per-type controls, no bespoke buttons.
  // Held / Available surfaces are hidden per ITER314; backend held-logic
  // remains untouched.
  const balance = wallet?.balance ?? 0;
  const inactive = wallet?.active === false;
  const deptHead = wallet?.department_head;
  const isSystem = wallet?.is_system;
  const canDelete = wallet?.can_delete;

  return (
    <Card className={`border ${inactive
        ? 'border-[#E2E8DF] bg-[#F4F4F2] opacity-70'
        : fallback ? 'border-amber-200 bg-amber-50/30' : 'border-[#E2E8DF]'}`}
      data-testid={testid}>
      <CardHeader className="pb-1.5">
        <CardTitle className="text-[12px] font-semibold text-[#1A2421] flex items-center justify-between gap-2">
          <span className="flex items-center gap-1.5 min-w-0">
            <span className="truncate">{label}</span>
            {inactive && (
              <span className="text-[9px] uppercase tracking-wider text-[#7A2E1F] bg-rose-50 border border-rose-200 rounded px-1 py-[1px] shrink-0">
                Disabled
              </span>
            )}
          </span>
          {isAdmin && wallet && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  type="button"
                  className="p-1 rounded hover:bg-[#F4F6F2] text-[#5C6B64] hover:text-[#1A2421] shrink-0"
                  data-testid={`${testid}-menu`}
                  aria-label="Wallet actions">
                  {toggling
                    ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    : <MoreVertical className="w-4 h-4" />}
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="min-w-[180px]">
                <DropdownMenuItem
                  onClick={onEdit}
                  data-testid={`${testid}-menu-settings`}
                  className="gap-2 text-[12px] cursor-pointer">
                  <Settings className="w-3.5 h-3.5" /> Wallet Settings
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={onToggleActive}
                  data-testid={`${testid}-menu-toggle`}
                  className={`gap-2 text-[12px] cursor-pointer ${inactive ? 'text-emerald-700' : 'text-rose-700'}`}>
                  {inactive
                    ? <><Power className="w-3.5 h-3.5" /> Enable</>
                    : <><PowerOff className="w-3.5 h-3.5" /> Disable</>}
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onClick={onDelete}
                  disabled={isSystem || !canDelete}
                  data-testid={`${testid}-menu-delete`}
                  className={`gap-2 text-[12px] ${isSystem || !canDelete
                    ? 'text-[#B8B8B5] cursor-not-allowed'
                    : 'text-rose-700 cursor-pointer'}`}>
                  <Trash2 className="w-3.5 h-3.5" /> Delete
                  {(isSystem || !canDelete) && (
                    <span className="ml-auto text-[9px] uppercase tracking-wider">
                      {isSystem ? 'System' : 'Has history'}
                    </span>
                  )}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
          {!wallet && (
            <span className="text-[9px] uppercase tracking-wider text-[#5C6B64] shrink-0">— —</span>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent className="pt-0 space-y-1">
        <div className="text-[10px] uppercase tracking-wider text-[#5C6B64]">Current Balance</div>
        <div className="text-[22px] font-semibold text-[#1A2421] tabular-nums" data-testid={`${testid}-balance`}>
          {fmt(balance)}
        </div>
        {deptHead && (
          <p className="text-[10px] text-[#5C6B64]">Head: <strong className="text-[#1A2421]">{deptHead}</strong></p>
        )}
        {sub && <p className="text-[10px] text-[#5C6B64] mt-1 leading-snug line-clamp-2">{sub}</p>}
      </CardContent>
    </Card>
  );
}



// ─── ITER314 · Custom Departmental Wallet dialog (create + edit) ────
function CustomWalletDialog({ open, mode, wallet, outlets, defaultOutletId, onClose, onSaved }) {
  const { getHeaders } = useAuth();
  const isCreate = mode === 'create';
  const [outletId, setOutletId] = useState('');
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [deptHead, setDeptHead] = useState('');
  const [opening, setOpening] = useState('');
  const [active, setActive] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open) return;
    if (isCreate) {
      setOutletId(defaultOutletId || outlets[0]?.id || '');
      setName(''); setDescription(''); setDeptHead('');
      setOpening(''); setActive(true);
    } else if (wallet) {
      setOutletId(wallet.outlet_id || '');
      setName(wallet.name || '');
      setDescription(wallet.description || '');
      setDeptHead(wallet.department_head || '');
      setOpening('');   // opening is not editable post-creation
      setActive(wallet.active !== false);
    }
    setError('');
  }, [open, isCreate, wallet, defaultOutletId, outlets]);

  const submit = async () => {
    setError('');
    if (isCreate && !outletId) return toast.error('Select an outlet');
    if (!name.trim() || name.trim().length < 2) return toast.error('Name must be at least 2 characters');
    setBusy(true);
    try {
      if (isCreate) {
        await axios.post(`${API}/petty-cash/wallets/custom`, {
          outlet_id: outletId,
          name: name.trim(),
          description: description.trim(),
          department_head: deptHead.trim() || null,
          opening_balance: Number(opening) || 0,
          active,
        }, { headers: getHeaders() });
        toast.success(`${name.trim()} wallet created`);
      } else {
        // ITER314.1 — unified PATCH endpoint works for every wallet
        // (system trio, Maintenance, customs). Name is skipped for system
        // wallets since the backend rejects renames on ops/store/admin.
        const body = {
          description: description.trim(),
          department_head: deptHead.trim() || null,
          active,
        };
        if (!wallet?.is_system) {
          body.name = name.trim();
        }
        await axios.patch(`${API}/petty-cash/wallets/${wallet.id}`, body, { headers: getHeaders() });
        toast.success('Wallet updated');
      }
      onSaved?.();
      onClose();
    } catch (err) {
      const detail = err.response?.data?.detail || 'Could not save wallet';
      setError(detail);
      toast.error(detail);
    } finally {
      setBusy(false);
    }
  };

  const isSystem = wallet?.is_system;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-md max-h-[90vh] overflow-y-auto" data-testid="custom-wallet-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-[#1A2421]">
            <Wallet className="w-4 h-4" /> {isCreate ? 'Create Departmental Wallet' : 'Wallet Settings'}
          </DialogTitle>
          <DialogDescription className="text-[#5C6B64]">
            {isCreate
              ? 'Every department manages its own petty cash allocation, vouchers and ledger.'
              : 'Update name, description, department head or activation state.'}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 mt-1">
          {isCreate ? (
            <div className="space-y-1.5">
              <Label className="text-sm text-[#5C6B64]">Outlet <span className="text-[#C45A41]">*</span></Label>
              <Select value={outletId} onValueChange={setOutletId}>
                <SelectTrigger className="bg-[#F9F9F8] border-[#E2E8DF] text-[12px]" data-testid="cw-outlet">
                  <SelectValue placeholder="Select outlet" />
                </SelectTrigger>
                <SelectContent>
                  {outlets.map(o => <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          ) : (
            <div className="rounded border border-[#E2E8DF] bg-[#F9F9F8] px-3 py-2 text-[11px] text-[#5C6B64]">
              Outlet: <strong className="text-[#1A2421]">
                {outlets.find(o => o.id === wallet?.outlet_id)?.name || '—'}
              </strong>
              <span className="mx-2">·</span>
              Slug: <code className="text-[#1A2421]">{wallet?.wallet_key}</code>
              <span className="ml-2 text-[10px]">(immutable)</span>
            </div>
          )}

          <div className="space-y-1.5">
            <Label className="text-sm text-[#5C6B64]">
              Wallet Name <span className="text-[#C45A41]">*</span>
              {isSystem && (
                <span className="ml-2 text-[10px] font-normal text-[#5C6B64] italic">
                  (system wallet · fixed name)
                </span>
              )}
            </Label>
            <Input value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Maintenance, Marketing, HR"
              readOnly={!!isSystem}
              disabled={!!isSystem}
              className={`bg-[#F9F9F8] border-[#E2E8DF] text-[12px] ${isSystem ? 'text-[#5C6B64] cursor-not-allowed' : ''}`}
              data-testid="cw-name" />
          </div>

          <div className="space-y-1.5">
            <Label className="text-sm text-[#5C6B64]">Description</Label>
            <Textarea rows={2} value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Optional context — what this wallet funds"
              className="bg-[#F9F9F8] border-[#E2E8DF] text-[12px]"
              data-testid="cw-description" />
          </div>

          <div className="space-y-1.5">
            <Label className="text-sm text-[#5C6B64]">Responsible Department Head</Label>
            <Input value={deptHead} onChange={(e) => setDeptHead(e.target.value)}
              placeholder="Optional — e.g. Priya S."
              className="bg-[#F9F9F8] border-[#E2E8DF] text-[12px]"
              data-testid="cw-dept-head" />
          </div>

          {isCreate && (
            <div className="space-y-1.5">
              <Label className="text-sm text-[#5C6B64]">Opening Balance (₹)</Label>
              <Input type="number" min="0" step="1" value={opening}
                onChange={(e) => setOpening(e.target.value)}
                placeholder="0"
                className="bg-[#F9F9F8] border-[#E2E8DF] text-[12px]"
                data-testid="cw-opening" />
              <p className="text-[10px] text-[#5C6B64]">
                Recorded as the first immutable ledger transaction (type: opening).
              </p>
            </div>
          )}

          <label className="flex items-center gap-2 text-[12px] text-[#1A2421] cursor-pointer">
            <input type="checkbox" checked={active}
              onChange={(e) => setActive(e.target.checked)}
              data-testid="cw-active" />
            Active
          </label>

          {error && (
            <div className="rounded border border-[#C45A41]/40 bg-[#C45A41]/[0.06] px-3 py-2 text-[12px] text-[#7A2E1F]"
              data-testid="cw-error">
              {error}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}
            className="border-[#E2E8DF] text-[#5C6B64]" data-testid="cw-cancel">Cancel</Button>
          <Button disabled={busy} onClick={submit}
            className="bg-[color:var(--color-primary)] hover:bg-[color:var(--color-primary-hover)] text-white"
            data-testid="cw-submit">
            {busy ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Check className="w-3 h-3 mr-1" />}
            {isCreate ? 'Create Wallet' : 'Save Changes'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
